
/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae notify page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useHistory, useParams } from 'react-router';


import Typography from '@material-ui/core/Typography';

import { Theme, makeStyles } from '@material-ui/core/styles';

import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes';
import { faUser } from '@fortawesome/pro-duotone-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, FormControlLabel, FormGroup, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { InstitutionContext } from '@ngt/online-patient-management-ui';
import { IRequestState, RequestState } from '@ngt/request-utilities';
import arrayMutators from 'final-form-arrays';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { Field, FormRenderProps, useForm } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { CheckboxWrapper } from 'src/components/FinalForm/FinalFormControls/CheckboxWrapper';
import PageLayout from 'src/components/Layout/PageLayout';
import * as Yup from 'yup';
import * as Dtos from '../../api/Neon.Dtos';
import client from '../../api/client';
import SaeBreadcrumbs from '../../components/Breadcrumbs/SaeBreadcrumbs';
import AsyncForm from '../../components/FinalForm/AsyncForm';
import { CheckboxListWrapper } from '../../components/FinalForm/FinalFormControls/CheckboxListWrapper';
import { CheckboxWithLabelWrapper } from '../../components/FinalForm/FinalFormControls/CheckboxWithLabelWrapper';
import TextFieldWrapper from '../../components/FinalForm/FinalFormControls/TextFieldWrapper';
import FileUpload from '../../components/OpmsForm/Fields/FileUpload';
import { useSaeById } from '../../store/modules/saereview/sae';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface ISaeNotifyPageParams {
    patientStudyNumber?: string,
    saeNumber?: string;
    notifyMode: string
}

interface ISaeNotifyPageProps {
}

interface INotifyReviewersFormProps extends FormRenderProps<Dtos.SaeNotificationForm> {
    reviewers?: Record<number, Dtos.Personnel>;
    loadState: IRequestState<Dtos.ResponseStatus>;
    onFormSave?: () => void;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    buttonSet: {
        textAlign: 'right',
        marginTop: theme.spacing(2)
    },
    button: {
        marginLeft: theme.spacing(1),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    },
    mt3: {
        marginTop: theme.spacing(3),
    },
    mt1: {
        marginTop: theme.spacing(1),
    },
    m2: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    table: {
        minWidth: '100%'
    },
    addButton: {
        textAlign: 'right',
        paddingTop: theme.spacing(3)
    },
    removeCell: {
        width: 95
    },
    remove: {
        color: theme.palette.error.main
    },
    hidden: {
        display: 'none'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const saePermissions = [Dtos.Permission.SaeView, Dtos.Permission.SaeReview, Dtos.Permission.SaeAdminister];

const bctEmailMatch = (window as any).SAE_NOTIFY.bctEmailAddressMatch;

const bctMedicalReviewSharedMailbox = (window as any).SAE_NOTIFY.bctMedicalReviewSharedMailbox;


const investigatorRoles = [8, 13, 18];

const medicalReviewerRoles = [48];

const useSaeReviewers = (institutionCode: string, roles: number []): [Record<number, Dtos.Personnel> | undefined, IRequestState<Dtos.ResponseStatus>] => {
    const [reviewers, setReviewers] = React.useState<Record<number, Dtos.Personnel> | undefined>(undefined);
    const [loadState, setLoadState] = React.useState<IRequestState<Dtos.ResponseStatus>>({ state: RequestState.None });

    useEffect(() => {
        const loadData = async () => {
            setLoadState({
                state: RequestState.Pending,
                responseStatus: undefined
            })

            try {
                const response = await client.get(new Dtos.GetInstitutionPersonnel({
                    institutionCode,
                    roles
                }))

                setReviewers(response.personnel)
                setLoadState({
                    state: RequestState.Success,
                    responseStatus: undefined
                })
            }
            catch(error: any) {
                setLoadState({
                    state: RequestState.Failure,
                    responseStatus: error?.responseStatus
                })
            }
        }

        loadData();
    }, [institutionCode, roles])

    return [
        reviewers,
        loadState
    ];
}

const SaeNotifyPage: React.FunctionComponent<ISaeNotifyPageProps> = () => {
    const classes = useStyles();

    const history = useHistory();

    const params = useParams<ISaeNotifyPageParams>();

    const [institution] = React.useContext(InstitutionContext);

    const [[, , canAdministerSae], saePermissionLoadState] = useBctApiPermissions(saePermissions);

    const [, , saeActions, ] = useSaeById(
        params.patientStudyNumber!!,
        +params.saeNumber!!
    );

    const [saeReviewers, saeReviewersLoadState] = useSaeReviewers(params.notifyMode === 'investigators' ? institution?.code ?? '' : '', params.notifyMode === 'investigators' ? investigatorRoles : medicalReviewerRoles);

    React.useEffect(() => {
        if (saePermissionLoadState.state !== RequestState.Pending &&
            saePermissionLoadState.state !== RequestState.None) {
            if (!canAdministerSae) {
                history.replace(`/error/403`);
            }
        }
    }, [canAdministerSae, saePermissionLoadState, history]);

    React.useEffect(() => {
        saeActions.load();

        return () => {
            saeActions.clear();
        }
    }, [saeActions]);

    const validation = Yup.object().shape<Dtos.SaeNotificationForm>({
        recipients: Yup.array().min(1, 'At least one recipient must be selected').of(Yup.number()),
        ccRecipients: Yup.array().min(0, '').of(Yup.number()),
        notes: Yup.string(),
        attachments: Yup.array().of(Yup.object().shape<Dtos.SourceDocument>({
            id: Yup.number(),
            patientId: Yup.number(),
            formTypeId: Yup.number(),
            formId: Yup.number(),
            fileTypeId: Yup.number(),
            extension: Yup.string(),
            name: Yup.string().required().label('File name'),
            guid: Yup.string().required().label('File'),
            fileSize: Yup.number(),
            contentType: Yup.string(),
            exists: Yup.boolean(),
            image: Yup.boolean(),
            enteredDate: Yup.string(),
            enteredByUserId: Yup.string(),
            modifiedDate: Yup.string(),
            modifiedByUserId: Yup.string()
        })),
        investigator: Yup.number(),
        sendBctEmailsToSharedMailbox: Yup.boolean()
    });

    const formSubscriptionParams = {
        submitting: true,
        pristine: false,
        submitError: true,
        dirtySinceLastSubmit: true,
        hasValidationErrors: true,
        valid: true,
        validating: true,
        errors: true,
        form: true
    };

    const initialNotifyReviewersForm: Dtos.SaeNotificationForm = React.useMemo(() => {
        return {
            recipients: [],
            ccRecipients: [],
            notes: '',
            attachments: [],
            investigator: params.notifyMode === 'investigators' ? 1 : 0,
            sendBctEmailsToSharedMailbox: params.notifyMode === 'mreviewer',
            notifySponsor: true
        };
    }, [params.notifyMode]);

    const onNotificationFormSave = React.useCallback(() => {
        history.push(`/sae/${institution?.code}/${params.patientStudyNumber}/${params.saeNumber}`)
    }, [institution, params, history]);

    const notifyReviewersForm = React.useCallback((props: FormRenderProps<Dtos.SaeNotificationForm>) => {
        return (
            <NotifyReviewersForm
                reviewers={saeReviewers}
                loadState={saeReviewersLoadState}
                onFormSave={onNotificationFormSave}
                {...props}
            />
        );
    }, [saeReviewers, saeReviewersLoadState, onNotificationFormSave]);

    return (
        <PageLayout
            breadcrumbs={
                <SaeBreadcrumbs />
            }
            heading="Serious Adverse Event"
        >
            <Paper
                className={classes.container}
            >
                <AsyncForm
                    initialValues={initialNotifyReviewersForm}
                    validate={validation}
                    onSubmit={saeActions.asyncNotify}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={notifyReviewersForm}
                    mutators={{
                        ...arrayMutators
                    }}
                />
            </Paper>
        </PageLayout>
    )
};

const NotifyReviewersForm: React.FunctionComponent<INotifyReviewersFormProps> = ({
    reviewers,
    loadState,
    onFormSave,
    ...formRenderProps
}) => {
    const classes = useStyles();

    const params = useParams<ISaeNotifyPageParams>();

    const reviewersValues = React.useMemo(() => {
        if (!reviewers) {
            return [];
        }

        return Object
            .keys(reviewers)
            .filter(i => reviewers[i] && reviewers[i].email)
            .map(i => ({
            value: parseInt(i, 10),
            label: (
                <div>
                    <FontAwesomeIcon icon={faUser} color={bctEmailMatch && reviewers[i].email.toLowerCase().includes(bctEmailMatch) ? 'green' : 'red'} style={{ marginRight: 8 }} />
                    {reviewers[i].name}
                </div>
            )
        }));
    }, [reviewers]);

    const { subscribe } = useForm()

    const { enqueueSnackbar } = useSnackbar();

    const [, , , ] = useSaeById(
        params.patientStudyNumber!!,
        +params.saeNumber!!
    );

    const onNotifyReviewersFormSend = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {

                enqueueSnackbar('Notification has been successfully sent.', { variant: 'success' });

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            enqueueSnackbar('An error occurred while attempting to send the notification.', { variant: 'error' });
        }
    }, [enqueueSnackbar, formRenderProps, onFormSave]);

    const remove = React.useCallback((index: number) => {
        formRenderProps.form.mutators.remove('attachments', index);
    }, [formRenderProps.form.mutators]);

    const onAddClick = React.useCallback(() => {
        formRenderProps.form.mutators.push('attachments', {})
    }, [formRenderProps.form.mutators])

    React.useEffect(() => {
        const unsubscribe = subscribe((_) => {}, { values: true });

        return () => unsubscribe();
    }, [subscribe])

    return (
        <>
            <Grid item xs={12}>
                <Typography
                    variant="h6"
                >
                    Please select recipients from the list below, enter notes if required, and click Send.
                    <br />
                    Recipients:
                </Typography>
            </Grid>
            <Field
                name="recipients"
                component={CheckboxListWrapper}
                values={reviewersValues}
            />
            <Grid item xs={12} className={classes.mt1}>
                <Typography
                    variant="h6"
                >
                    CC Recipients:
                </Typography>
            </Grid>
            <Field
                name="ccRecipients"
                component={CheckboxListWrapper}
                values={reviewersValues}
            />
            <Grid item xs={12} className={classes.m2}>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Field
                                name="notifySponsor"
                                component={CheckboxWrapper}
                                label="Notify Sponsor (BMS)"
                            />
                        }
                        label="Notify Sponsor (BMS)"
                    />
                </FormGroup>
            </Grid>
            <Grid item xs={12} className={classes.mt1}>
                <Typography
                    variant="h6"
                >
                    Attach Documents:
                </Typography>
            </Grid>
            <Table
                className={classes.table}
            >
                <TableHead>
                    <TableRow>
                        {
                            <TableCell>
                                <Typography>
                                    <strong>
                                        Name
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                        {
                            <TableCell>
                                <Typography>
                                    <strong>
                                        File
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                        {
                            <TableCell
                                className={classes.removeCell}
                            >
                                <Typography>
                                    <strong>
                                        Remove
                                    </strong>
                                </Typography>
                            </TableCell>
                        }
                    </TableRow>
                </TableHead>
                <TableBody>
                    <FieldArray name="attachments">
                        {({ fields }) => {
                            return fields.map((field, index) => {
                                const onRemoveClick = () => {
                                    remove(index);
                                };

                                return (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Field
                                                fullWidth
                                                name={`${field}.name`}
                                                component={TextFieldWrapper}
                                                type="text"
                                                label="File name"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Field
                                                fullWidth
                                                name={`${field}.guid`}
                                                component={FileUpload}
                                                label="File"
                                                existsFieldName="Exists"
                                                fileIdFieldName="Id"
                                                imageFieldName="Image"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {
                                                (index >= 0) ?
                                                    <Button
                                                        variant="text"
                                                        color="default"
                                                        className={classes.remove}
                                                        onClick={onRemoveClick}
                                                    >
                                                        <FontAwesomeIcon fixedWidth icon={faTimes} />
                                                    </Button> :
                                                    null
                                            }
                                        </TableCell>
                                    </TableRow>
                                )
                            });
                        }}
                    </FieldArray>
                </TableBody>
            </Table>
            <Field
                name="investigator"
                className={classes.hidden}
                component="input"
                value={1}
            />
            <div
                className={classes.addButton}
            >
                <Button
                    variant="contained"
                    type="button"
                    color="primary"
                    onClick={onAddClick}
                >
                    Add
                </Button>
            </div>
            <Grid item xs={12} className={classes.mt1}>
                <Typography
                    variant="h6"
                >
                    BCT Notes{ params.notifyMode === 'mreviewer' ? ' for medical reviewers' : '' }:
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Field
                    fullWidth
                    name="notes"
                    component={TextFieldWrapper}
                    type="text"
                    rows={10}
                    multiline={true}
                    variant="outlined"
                />
            </Grid>
            {
                params.notifyMode === 'mreviewer' && bctMedicalReviewSharedMailbox && bctEmailMatch && (
                    <Field
                        name="sendBctEmailsToSharedMailbox"
                        component={CheckboxWithLabelWrapper}
                        label={<>
                            Send emails addressed to BCT personnel (<FontAwesomeIcon icon={faUser} color="green" />) to the BCT shared mailbox ({bctMedicalReviewSharedMailbox}).
                        </>}
                    />
                )
            }
            <div className={classes.buttonSet}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onNotifyReviewersFormSend}>
                    SEND
                </Button>
            </div>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default SaeNotifyPage;